import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc, collection, query, where, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../contexts/AuthContext';
import LoadingIndicator from '../common/LoadingIndicator';
import { v4 as uuidv4 } from 'uuid';
import { getExistingCertificate } from '../../utils/certificateCleanup';
import { onSnapshot } from 'firebase/firestore';

// Dynamically import html2canvas and jsPDF (promise vars to avoid re-import confusion)
const html2canvasPromise = import('html2canvas');
const jsPDFPromise = import('jspdf');

function Certificate() {
  const { courseId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [courseData, setCourseData] = useState(null);
  const [completionData, setCompletionData] = useState(null);
  const [verificationId, setVerificationId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [certificateRecord, setCertificateRecord] = useState(null);

  const verifyUrl = useMemo(() => (
    verificationId ? `https://iofellowship.org/verify/${verificationId}` : ''
  ), [verificationId]);

  const displayName = useMemo(() => {
    const primary = user?.displayName && user.displayName.trim().length > 0
      ? user.displayName
      : '';
    if (primary) return primary;
    const email = user?.email || '';
    if (!email) return 'Learner';
    const handle = email.includes('@') ? email.split('@')[0] : email;
    return handle || 'Learner';
  }, [user]);

  useEffect(() => {
    const fetchCertificateData = async () => {
      if (!user || !courseId) {
        console.log('Missing user or courseId:', { user, courseId });
        setLoading(false);
        return;
      }

      try {
        console.log('Fetching certificate data for:', { userId: user.uid, courseId });
        
        // Verify course completion
        const completedRef = doc(db, 'users', user.uid, 'completedSubCourses', courseId);
        const completedDoc = await getDoc(completedRef);

        if (!completedDoc.exists()) {
          console.log('Course completion document not found');
          setErrorMessage('Course must be completed to view certificate.');
          navigate(`/courses/${courseId}`);
          return;
        }

        // Fetch course details
        const courseRef = doc(db, 'courses', courseId);
        const courseDoc = await getDoc(courseRef);

        if (!courseDoc.exists()) {
          console.log('Course document not found');
          throw new Error('Course not found');
        }

        const courseData = courseDoc.data();
        const completionData = completedDoc.data();

        console.log('Retrieved data:', { courseData, completionData });

        // Get completion timestamp, fallback to current time if not available
        const completionTimestamp = completionData.firstCompletedAt || 
                                  completionData.completedAt || 
                                  new Date().toISOString();

        // Check for existing certificate and clean up duplicates
        const existingCert = await getExistingCertificate(user.uid, courseId);
        let certVerificationId;

        if (existingCert) {
          // Use existing certificate
          certVerificationId = existingCert.data().verificationId;
          console.log('Using existing certificate:', certVerificationId);
          // Live-subscribe to certificate doc so Firestore edits reflect in UI
          try {
            const certificateRef = doc(db, 'certificates', certVerificationId);
            const unsubscribe = onSnapshot(certificateRef, (snap) => {
              if (snap.exists()) {
                setCertificateRecord({ id: snap.id, ...snap.data() });
              }
            });
            // Clean up subscription on unmount or course/user change
            // Store unsubscribe in a closure var to call below
            (window.__iof_cert_unsub || (window.__iof_cert_unsub = {}))[`${user.uid}_${courseId}`] = unsubscribe;
          } catch (subErr) {
            console.warn('Certificate live subscription failed:', subErr);
          }
        } else {
          // Do not create certificates here to avoid race conditions and duplicates.
          console.warn('No existing certificate found. Redirecting back to course page.');
          setErrorMessage('Certificate not found yet. Please try again from the course page.');
          navigate(`/courses/${courseId}`);
          return;
        }

        // Update state
        setCourseData(courseData);
        setCompletionData({
          ...completionData,
          firstCompletedAt: completionTimestamp
        });
        setVerificationId(certVerificationId);
        
        console.log('Certificate data fetched and processed successfully');
      } catch (error) {
        console.error('Detailed error in fetchCertificateData:', {
          error,
          errorMessage: error.message,
          errorCode: error.code,
          errorStack: error.stack
        });
        setErrorMessage(`Error loading certificate: ${error.message}`);
        navigate('/courses');
      } finally {
        setLoading(false);
      }
    };

    fetchCertificateData();
  }, [courseId, user, navigate]);

  // Cleanup any prior subscription when component unmounts or deps change
  useEffect(() => {
    return () => {
      try {
        const key = user && courseId ? `${user.uid}_${courseId}` : null;
        const map = window.__iof_cert_unsub;
        if (key && map && typeof map[key] === 'function') {
          map[key]();
          delete map[key];
        }
      } catch {}
    };
  }, [user, courseId]);

  const handleDownload = async () => {
    try {
      const certificate = document.getElementById('certificate');
      const h2c = (await html2canvasPromise).default;
      const PDF = (await jsPDFPromise).default;

      // Make certificate visible for capture
      const originalDisplay = certificate.style.display;
      certificate.style.display = 'block';

      // Simple configuration for html2canvas
      const canvas = await h2c(certificate, {
        scale: 3,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        removeContainer: true,
        foreignObjectRendering: false // Disable foreign object rendering
      });

      // Restore original display
      certificate.style.display = originalDisplay;

      // Create PDF
      const pdf = new PDF({
        orientation: 'landscape',
        unit: 'px',
        format: [1056, 747]
      });

      // Add image to PDF
      const imgData = canvas.toDataURL('image/png');
      pdf.addImage(imgData, 'PNG', 0, 0, 1056, 747);
      
      // Save PDF
      pdf.save(`${courseData?.title || 'Certificate'}.pdf`);

    } catch (error) {
      console.error('Detailed download error:', error);
      setErrorMessage('Failed to download certificate. Please try again.');
    }
  };

  if (loading) {
    return <LoadingIndicator />;
  }

  return (
    <div className="min-h-screen bg-gray-100 p-4 sm:p-6 flex flex-col items-center">
      <div className="w-full max-w-[1200px] mx-auto">
        {errorMessage && (
          <div className="mb-4 rounded-md border border-red-300 bg-red-50 text-red-700 px-4 py-3 flex items-start justify-between gap-4">
            <div className="text-sm">{errorMessage}</div>
            <button onClick={() => setErrorMessage('')} className="text-xs underline hover:no-underline">Dismiss</button>
          </div>
        )}
        {/* Download Button */}
        <div className="mb-4 sm:mb-6 text-center">
          <button
            onClick={handleDownload}
            className="bg-[rgb(130,88,18)] text-white px-6 py-3 rounded-full hover:bg-[rgb(110,68,0)] transition-all shadow-md hover:shadow-lg hover:scale-105 flex items-center justify-center gap-2 mx-auto text-base"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Download Certificate
          </button>
          {/* Share buttons */}
          {verificationId && (
            <div className="mt-3 flex items-center justify-center gap-3 text-sm">
              <a
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(verifyUrl)}`}
                target="_blank"
                rel="noreferrer"
                className="px-3 py-1 rounded-full border border-gray-300 hover:border-gray-400 hover:bg-gray-50 transition-colors"
              >
                Share on LinkedIn
              </a>
              <a
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent('I just earned an Intellectual Oasis Fellowship course certificate!')}%20&url=${encodeURIComponent(verifyUrl)}`}
                target="_blank"
                rel="noreferrer"
                className="px-3 py-1 rounded-full border border-gray-300 hover:border-gray-400 hover:bg-gray-50 transition-colors"
              >
                Share on X
              </a>
            </div>
          )}
        </div>


        {/* Certificate Container - Hidden on Mobile, Visible on Larger Screens */}
        <div 
          id="certificate"
          className="bg-white relative mx-auto hidden sm:block"
          style={{ 
            width: '1056px',
            height: '747px',
            padding: '40px 60px',
            border: '2px solid transparent',
            borderImage: 'linear-gradient(90deg, #c19a6b, #825812) 1',
            backgroundColor: '#ffffff',
            position: 'relative',
            margin: '0 auto',
            transformOrigin: 'top center',
          }}
        >
          {/* Watermark */}
          <img
            src="/images/logo.jpg"
            alt="Intellectual Oasis Fellowship Watermark"
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '60%',
              height: 'auto',
              opacity: 0.05,
              pointerEvents: 'none',
              userSelect: 'none'
            }}
            crossOrigin="anonymous"
          />
          {/* Intellectual Oasis Fellowship Logo */}
          <img 
            src="/images/logo.jpg"
            alt="Intellectual Oasis Fellowship Logo" 
            style={{
              position: 'absolute',
              top: '32px',
              left: '32px',
              width: '150px',
              height: 'auto',
              objectFit: 'contain',
              objectPosition: 'left top',
              display: 'block',
              zIndex: 10
            }}
            crossOrigin="anonymous"
          />

          {/* Certificate Content */}
          <div className="h-full flex flex-col justify-center items-center">
            <div className="text-center mb-8">
              <div 
                style={{
                  border: '1px solid rgb(130,88,18)',
                  padding: '1rem 2rem',
                  backgroundColor: '#f9f9f9'
                }}
              >
                <h2 className="text-base font-serif text-gray-600">VERIFIED CERTIFICATE</h2>
                <div className="mt-0.5 text-sm" style={{ color: 'rgb(130,88,18)' }}>INTELLECTUAL OASIS FELLOWSHIP</div>
              </div>
            </div>

            <div className="text-center space-y-4 max-w-2xl">
              <div className="text-sm font-serif tracking-wide text-gray-600">
                {(certificateRecord?.completedAt || completionData?.firstCompletedAt) ? 
                  new Date(certificateRecord?.completedAt || completionData.firstCompletedAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  }) 
                  : new Date().toLocaleDateString()
                }
              </div>
              
              <h2 className="text-5xl font-serif font-extrabold text-gray-800">
                {certificateRecord?.userName || displayName}
              </h2>
              
              <p className="text-lg text-gray-600">
                has successfully completed with distinction
              </p>
              
              <h3 className="text-2xl italic tracking-wide font-semibold text-[rgb(130,88,18)]">
                {certificateRecord?.courseName || courseData?.title || 'Unnamed Course'}
              </h3>
              
              <p className="text-base text-gray-600">
                a curated online course offered and compiled by Intellectual Oasis Fellowship
              </p>
            </div>

            <div className="flex justify-between items-end w-full mt-12">
              <div className="text-center">
                <div className="mb-2">
                  <img src="/images/signature.png" alt="Signature" className="h-10 mx-auto" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                </div>
                <div style={{ borderTop: '1px solid #333', width: '120px' }}></div>
                <p className="text-xs text-gray-600 mt-1">Olalekan L. Adeyinka</p>
                <p className="text-xs text-gray-600">Director, Intellectual Oasis Fellowship</p>
              </div>

              <div className="text-center">
                <div className="w-[110px] aspect-square mb-2 mx-auto">
                  <svg viewBox="0 0 120 120" style={{ width: '100%', height: '100%' }}>
                    <defs>
                      <radialGradient id="goldGrad" cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="#fff4d6"/>
                        <stop offset="60%" stopColor="#c19a6b"/>
                        <stop offset="100%" stopColor="#825812"/>
                      </radialGradient>
                    </defs>
                    <circle cx="60" cy="60" r="55" fill="url(#goldGrad)" stroke="#825812" strokeWidth="2"/>
                    <circle cx="60" cy="60" r="45" fill="none" stroke="#fff" strokeOpacity="0.6" strokeWidth="2"/>
                    <text x="60" y="54" textAnchor="middle" fill="#4b3410" fontSize="24" fontFamily="serif">IOF</text>
                    <text x="60" y="74" textAnchor="middle" fill="#4b3410" fontSize="14" fontFamily="serif">Verified</text>
                  </svg>
                </div>
                <a href={verifyUrl} target="_blank" rel="noreferrer" className="text-xs text-gray-600 hover:underline hover:text-[rgb(130,88,18)]">
                  Verify at iofellowship.org/verify/
                </a>
                <p className="text-[10px] text-gray-500">Certificate ID: {verificationId}</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Mobile Message */}
        <div className="sm:hidden text-center mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-gray-700 text-sm">
            Certificate preview is not available on mobile devices. 
            Use the download button above to get your certificate.
          </p>
        </div>
      </div>
    </div>
  );
}

export default Certificate; 